// frontend/src/utils/conversation/shouldBypassIntentFallback.js

function normalizeText(value = '') {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ');
}

function countWords(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

function hasResolvedEntity(entity = null) {
  return entity?.signals?.resolved === true && !!entity?.name;
}

function hasDominantEmotionalDisclosure(turnSignalArbitration = null) {
  return (
    turnSignalArbitration?.dominantTurnMode === 'emotional_disclosure' ||
    (turnSignalArbitration?.scores?.emotionalDisclosure || 0) >= 0.7
  );
}

function hasDominantContextualContinuation(turnSignalArbitration = null) {
  return (
    turnSignalArbitration?.dominantTurnMode === 'contextual_continuation' ||
    (turnSignalArbitration?.scores?.contextualContinuation || 0) >= 0.65
  );
}

function hasDominantTopicContinuation(turnSignalArbitration = null) {
  return (
    turnSignalArbitration?.dominantTurnMode === 'topic_continuation' ||
    (turnSignalArbitration?.scores?.topicContinuation || 0) >= 0.6 ||
    turnSignalArbitration?.control?.shouldPreserveTopicContinuity === true
  );
}

function isQuestionLike(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;
  if (String(message || '').includes('?')) return true;

  return /^(what|why|how|when|where|who|can you|could you|would you|will you|do you|did you|are you|is it)\b/.test(
    lower
  );
}

function containsLowAuthorityShorthand(message = '') {
  const lower = normalizeText(message);
  if (!lower) return false;

  return /\b(u|ur|ya|tho|bc|cuz)\b/.test(lower);
}

function isLikelyConversationalContinuation(message = '') {
  const lower = normalizeText(message);
  const words = countWords(lower);

  if (!lower) return false;
  if (isQuestionLike(lower)) return false;
  if (words < 4) return false;

  return true;
}

function shouldBypassIntentFallback({
  message = '',
  turnAttachment = null,
  lastReferencedEntity = null,
  turnSignalArbitration = null,
  pronounResolutionSignal = null,
} = {}) {
  const resolvedAttachmentEntity = turnAttachment?.resolvedEntity || null;

  // Canonical resolved truth wins
  if (
    hasResolvedEntity(resolvedAttachmentEntity) ||
    hasResolvedEntity(lastReferencedEntity)
  ) {
    return true;
  }

  // Arbitration is the source of truth now
  if (turnSignalArbitration?.control?.shouldBypassFallback === true) {
    return true;
  }

  // Strong continuity lanes should never hit fallback
  if (hasDominantTopicContinuation(turnSignalArbitration)) {
    return true;
  }

  if (hasDominantContextualContinuation(turnSignalArbitration)) {
    return true;
  }

  // Composite continuity: no single signal won hard enough,
  // but together they clearly indicate this is not a fallback turn
  const hasCompositeContinuation =
    (turnSignalArbitration?.scores?.topicContinuation || 0) >= 0.45 &&
    (turnSignalArbitration?.scores?.contextualContinuation || 0) >= 0.45;

  if (hasCompositeContinuation) {
    return true;
  }

  // Low-authority shorthand / typo-like casual phrasing should not be promoted
  // into a memory target when continuity already exists
  const hasActiveContinuity =
    (turnSignalArbitration?.scores?.topicContinuation || 0) >= 0.35 ||
    (turnSignalArbitration?.scores?.contextualContinuation || 0) >= 0.35 ||
    !!turnSignalArbitration?.signals?.activeTopic ||
    !!turnSignalArbitration?.signals?.activeEntity;

  if (
    hasActiveContinuity &&
    containsLowAuthorityShorthand(message) &&
    isLikelyConversationalContinuation(message)
  ) {
    return true;
  }

  // Strong emotional lane should never hit fallback
  if (hasDominantEmotionalDisclosure(turnSignalArbitration)) {
    return true;
  }

  // Strong pronoun continuity wins
  if (pronounResolutionSignal?.shouldBypassFallback === true) {
    return true;
  }

  if (
    pronounResolutionSignal?.resolved === true &&
    (pronounResolutionSignal?.score || 0) >= 0.7
  ) {
    return true;
  }

  if (turnSignalArbitration?.control?.shouldPreservePronounContinuity === true) {
    return true;
  }

  return false;
}

function buildAttachedTurnIntent({
  lastReferencedEntity = null,
  lastReferencedTopic = null,
  pronounResolutionSignal = null,
} = {}) {
  const isPronounBound =
    pronounResolutionSignal?.resolved === true &&
    (pronounResolutionSignal?.score || 0) >= 0.7;

  return {
    intent: 'memory_recall',
    target: lastReferencedEntity?.role || null,
    memoryName: lastReferencedEntity?.name || null,
    source: isPronounBound ? 'pronoun_continuity' : 'turn_attachment',
    inheritedEntity: lastReferencedEntity || null,
    inheritedTopic: lastReferencedTopic || null,
    vectorHints: [],
    continuity: {
      pronounBound: isPronounBound,
      pronouns: pronounResolutionSignal?.pronouns || [],
      score: pronounResolutionSignal?.score || 0,
    },
  };
}

export { shouldBypassIntentFallback, buildAttachedTurnIntent };